// src/components/employee/EmployeeTeam.js
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Users, Mail, Phone, Calendar, CheckCircle, XCircle } from 'lucide-react';
import { api } from '../../services/api';
import { formatTime, formatDate } from '../../utils/dateHelpers';

const EmployeeTeam = () => {
    const [teamMembers, setTeamMembers] = useState([]);
    const [teamAttendance, setTeamAttendance] = useState([]);
    const [teamLeaves, setTeamLeaves] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('members');
    const tabLocation = useLocation();
    useEffect(() => {
        const params = new URLSearchParams(tabLocation.search);
        const tab = params.get('tab');
        if (tab) setActiveTab(tab);
    }, [tabLocation.search]);

    useEffect(() => {
        fetchTeamData();
    }, []);

    const fetchTeamData = async () => {
        try {
            const [membersRes, attendanceRes, leavesRes] = await Promise.all([
                api.get('/employees/team'),
                api.get('/attendance/team'),
                api.get('/leaves/team')
            ]);

            setTeamMembers(membersRes.data.teamMembers || []);
            setTeamAttendance(attendanceRes.data.attendance || []);
            setTeamLeaves(leavesRes.data.requests || []);
        } catch (error) {
            console.error('Error fetching team data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleLeaveApproval = async (requestId, action) => {
        try {
            await api.post(`/leaves/${action}/${requestId}`);
            fetchTeamData();
        } catch (error) {
            console.error(`Error ${action}ing leave:`, error);
        }
    };

    if (loading) {
        return <div className="flex justify-center items-center h-64">Loading...</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-900">My Team</h1>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Users className="w-4 h-4" />
                    {teamMembers.length} Team Members
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="border-b">
                <nav className="flex space-x-8">
                    {['members', 'attendance', 'leaves'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === tab
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Team Members */}
            {activeTab === 'members' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {teamMembers.map((member) => (
                        <div key={member.id} className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center mb-4">
                                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold mr-4">
                                    {member.first_name?.[0]}{member.last_name?.[0]}
                                </div>
                                <div>
                                    <h3 className="font-semibold">{member.first_name} {member.last_name}</h3>
                                    <p className="text-sm text-gray-500">{member.designation}</p>
                                </div>
                            </div>

                            <div className="space-y-2 text-sm">
                                <div className="flex items-center text-gray-600">
                                    <Mail className="w-4 h-4 mr-2" />
                                    {member.email}
                                </div>
                                <div className="flex items-center text-gray-600">
                                    <Phone className="w-4 h-4 mr-2" />
                                    {member.phone || 'Not provided'}
                                </div>
                                <div className="flex items-center text-gray-600">
                                    <Users className="w-4 h-4 mr-2" />
                                    {member.department_name}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Team Attendance */}
            {activeTab === 'attendance' && (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <table className="min-w-full">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check In</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check Out</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {teamAttendance.map((record) => (
                            <tr key={record.id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div>
                                        <p className="text-sm font-medium">{record.first_name} {record.last_name}</p>
                                        <p className="text-xs text-gray-500">{record.employee_code}</p>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    {record.check_in_time ? formatTime(record.check_in_time) : '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    {record.check_out_time ? formatTime(record.check_out_time) : '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        record.status === 'present' ? 'bg-green-100 text-green-800' :
                            record.status === 'absent' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                    }`}>
                      {record.status}
                    </span>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Team Leave Requests */}
            {activeTab === 'leaves' && (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b">
                        <h3 className="font-semibold">Pending Leave Requests</h3>
                    </div>
                    <table className="min-w-full">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {teamLeaves.map((request) => (
                            <tr key={request.id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <p className="text-sm font-medium">{request.first_name} {request.last_name}</p>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">{request.leave_type}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    {formatDate(request.from_date)} - {formatDate(request.to_date)}
                                </td>
                                <td className="px-6 py-4 text-sm">{request.reason}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleLeaveApproval(request.id, 'approve')}
                                            className="text-green-600 hover:text-green-900"
                                        >
                                            <CheckCircle className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => handleLeaveApproval(request.id, 'reject')}
                                            className="text-red-600 hover:text-red-900"
                                        >
                                            <XCircle className="w-5 h-5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default EmployeeTeam;